import { cn, Container, Loader, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';
import { Route, Routes } from 'react-router-dom';

import { SecondaryNav, SuspenseBoundary, TabsList, TabTrigger } from '@/components';
import { usePathSet } from '@/hooks/usePathSet';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { useMenuItems } from '@/pages/shared/hooks/useMenuItems';
import { useProjectId } from '@/pages/shared/hooks/useProjectId';
import { useIsRestorationProject } from '@/pages/shared/projects/pages/project/pages/conservation/restoration/hooks/useIsRestorationProject';
import { ProjectMap } from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/ProjectMap';
import { ProjectOverview } from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/ProjectOverview';
import { useFact } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useFact';
import { useProject } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useProject';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';

import { LandMonitoringPage } from './pages/land-monitoring.page';
import { NcaPage } from './pages/nca.page';
import { OverviewPage } from './pages/overview.page';

export const ConservationProjectPage = () => {
  const { t } = useTranslation();

  const menuItems = useMenuItems();
  const projectId = useProjectId();
  const project = useProject();
  const pathSet = usePathSet();
  const isRestorationProject = useIsRestorationProject();

  const natureUnitsCount = useFact('r1_conservation_no_nature_units');
  const ecologicalIntegrityAverageCurrent = useFact('r1_conservation_ecological_integrity');

  const hasNCAData = !isNullish(natureUnitsCount?.value) || !isNullish(ecologicalIntegrityAverageCurrent?.value);

  const showNCAPageTab = !isRestorationProject && hasNCAData;

  return (
    <SignedInPageLayout items={menuItems} withCustomContentContainer>
      <div className='flex flex-col h-full'>
        <SecondaryNav title={project?.name} backPath={pathSet.projects} />
        <div className='flex-1 overflow-y-auto'>
          <Container gutterWidth={{ sm: 4 }} className='mt-10' data-testid='conservation-project-page'>
            <Stack spacing={10}>
              <div className={cn('grid grid-rows-[auto] gap-10', 'md:w-auto md:grid-cols-2')}>
                <ProjectMap />
                <ProjectOverview />
              </div>

              <Stack>
                <TabsList className='mb-10 justify-start sm:justify-center'>
                  <TabTrigger path={buildPath(pathSet.projectDetails, { pathParams: { projectId } })}>
                    {t('shared.projects.labels.overview')}
                  </TabTrigger>
                  {showNCAPageTab && (
                    <TabTrigger path={buildPath(pathSet.ncaProjects, { pathParams: { projectId } })}>
                      {t('shared.projects.labels.naturalCapitalAccount')}
                    </TabTrigger>
                  )}
                  <TabTrigger path={buildPath(pathSet.landMonitoring, { pathParams: { projectId } })}>
                    {t('shared.projects.labels.landMonitoring')}
                  </TabTrigger>
                </TabsList>

                <SuspenseBoundary
                  fallback={
                    <Stack center className='h-[600px]'>
                      <Loader />
                    </Stack>
                  }
                >
                  <Routes>
                    <Route index element={<OverviewPage />} />
                    {showNCAPageTab && <Route path='nca' element={<NcaPage />} />}
                    <Route path='land-monitoring' element={<LandMonitoringPage />} />
                  </Routes>
                </SuspenseBoundary>
              </Stack>
            </Stack>
          </Container>
        </div>
      </div>
    </SignedInPageLayout>
  );
};
